import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Star, Tv, Baby, Crown, Sparkles, Globe, Smartphone } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user) {
    navigate("/browse");
    return null;
  }

  const features = [
    { icon: Tv, title: "Live TV & Streams", desc: "Watch news, sports, music and more from live channels worldwide." },
    { icon: Sparkles, title: "Cinema-grade Quality", desc: "Stream in up to 4K HDR with crystal-clear sound on every device." },
    { icon: Baby, title: "Safe Kids Zone", desc: "A protected playground of cartoons, learning shows, and family fun." },
    { icon: Globe, title: "African Stories", desc: "Curated Congolese & African originals you won't find anywhere else." },
    { icon: Smartphone, title: "Watch Anywhere", desc: "Mobile, tablet, laptop, smart TV — Djino follows you everywhere." },
    { icon: Crown, title: "Premium Originals", desc: "Award-worthy series and films produced just for our community." },
  ];

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="landing-page">
      {/* Top mini-nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-12 py-5 flex items-center justify-between glass-nav">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#E50914] to-[#8B0000] flex items-center justify-center shadow-[0_0_20px_rgba(229,9,20,0.5)]">
            <span className="text-white font-black text-lg" style={{ fontFamily: "Outfit" }}>D</span>
          </div>
          <span className="text-xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            Djino<span className="text-[#E50914]">Stream</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-[#ccc] hover:text-white" data-testid="landing-login-link">Sign In</Link>
          <Link to="/signup" className="btn-primary !py-2 !px-4 !text-sm" data-testid="landing-signup-btn">Start Watching</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1776780374903-8883c861b436?w=1920&q=85"
            alt="hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/60 to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-12 w-full">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-block text-xs font-bold text-[#E50914] uppercase tracking-[0.3em] mb-4">A new era of streaming</span>
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: "Outfit" }}>
              Cinema in your pocket. <span className="text-[#E50914]">Stories</span> on your screen.
            </h1>
            <p className="text-lg sm:text-xl text-[#ccc] leading-relaxed mb-8 max-w-xl">
              Movies, TV shows, live channels and a dedicated kids zone — all in one beautifully crafted home. Welcome to <span className="text-white font-semibold">Djino Stream</span>.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/signup" className="btn-primary text-base !py-3.5 !px-6 justify-center" data-testid="hero-signup-btn">
                <Play size={18} className="fill-white" /> Start your free trial
              </Link>
              <Link to="/login" className="btn-secondary text-base !py-3.5 !px-6 justify-center" data-testid="hero-signin-btn">
                Sign in
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-[#888]">
              <div className="flex items-center gap-1.5"><Star size={14} className="fill-yellow-400 text-yellow-400" />4.9 / 5 user rating</div>
              <div>Cancel anytime</div>
              <div>Watch on 4 screens</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-24 px-6 sm:px-12 max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-[#E50914] uppercase tracking-[0.3em]">Why Djino</span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mt-3" style={{ fontFamily: "Outfit" }}>Everything you love. In one stream.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="bg-[#0d0d0d] border border-white/5 rounded-xl p-7 hover:border-[#E50914]/40 hover:-translate-y-1 transition-all" data-testid={`feature-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#E50914]/15 text-[#E50914] flex items-center justify-center mb-4">
                <f.icon size={22} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "Outfit" }}>{f.title}</h3>
              <p className="text-sm text-[#aaa] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans preview */}
      <section className="py-24 px-6 sm:px-12 max-w-[1400px] mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-[#E50914] uppercase tracking-[0.3em]">Plans</span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mt-3" style={{ fontFamily: "Outfit" }}>Pick the plan that fits.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { name: "Basic", price: "4.99", desc: "HD streaming on 1 screen.", features: ["Watch on phone, tablet, laptop", "HD Quality", "Limited downloads"] },
            { name: "Standard", price: "9.99", desc: "Full HD on 2 screens.", features: ["Full HD", "2 simultaneous screens", "Unlimited downloads"], featured: true },
            { name: "Premium", price: "14.99", desc: "4K + HDR on 4 screens.", features: ["4K + HDR", "4 simultaneous screens", "Spatial audio"] },
          ].map((p) => (
            <div key={p.name} className={`p-7 rounded-2xl border ${p.featured ? "border-[#E50914] bg-gradient-to-b from-[#E50914]/15 to-[#0d0d0d]" : "border-white/5 bg-[#0d0d0d]"}`} data-testid={`plan-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{p.name}</h3>
                {p.featured && <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-wider">Most Popular</span>}
              </div>
              <div className="mb-3">
                <span className="text-4xl font-black" style={{ fontFamily: "Outfit" }}>${p.price}</span>
                <span className="text-sm text-[#999]"> /mo</span>
              </div>
              <p className="text-sm text-[#aaa] mb-4">{p.desc}</p>
              <ul className="space-y-2 mb-5">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-[#ccc] flex items-center gap-2"><span className="text-[#E50914]">✓</span> {f}</li>
                ))}
              </ul>
              <Link to="/signup" className={`block text-center py-3 rounded-md font-semibold transition ${p.featured ? "bg-[#E50914] hover:bg-[#F6121D] text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}>
                Choose {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 sm:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-6xl font-black tracking-tighter mb-4" style={{ fontFamily: "Outfit" }}>Ready to dive in?</h2>
          <p className="text-lg text-[#bbb] mb-7">Join thousands streaming with Djino every day.</p>
          <Link to="/signup" className="btn-primary !py-4 !px-7 text-base" data-testid="cta-signup-btn">
            <Play size={18} className="fill-white" /> Start Watching Now
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-[#666]">
        © 2026 Djino Stream — Original premium streaming.
      </footer>
    </div>
  );
}
